import React, { useState, useContext, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Forward, Reply, Trash2, Smile, Pencil, Loader2 } from "lucide-react";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import { Button } from "../ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import ForwardMessageModal from "../ForwardMessageModal";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

// Os seis que cobrem quase toda reação no WhatsApp. Mais que isso vira uma
// fileira que não cabe na largura do menu.
const RAPIDOS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

// Mantém a API antiga (anchorEl vindo do MessagesList) sem depender do MUI:
// o menu é posicionado a partir do retângulo do elemento âncora.
const MessageOptionsMenu = ({ message, menuOpen, handleClose, anchorEl }) => {
	const { setReplyingMessage } = useContext(ReplyMessageContext);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [encaminhar, setEncaminhar] = useState(false);
	const [editando, setEditando] = useState(false);
	const [textoEditado, setTextoEditado] = useState("");
	const [salvandoEdicao, setSalvandoEdicao] = useState(false);
	const menuRef = useRef(null);

	useEffect(() => {
		if (!menuOpen) return undefined;

		const aoClicarFora = (e) => {
			if (menuRef.current && !menuRef.current.contains(e.target)) {
				handleClose();
			}
		};
		const aoTeclar = (e) => e.key === "Escape" && handleClose();

		document.addEventListener("mousedown", aoClicarFora);
		document.addEventListener("keydown", aoTeclar);

		return () => {
			document.removeEventListener("mousedown", aoClicarFora);
			document.removeEventListener("keydown", aoTeclar);
		};
	}, [menuOpen, handleClose]);

	// Prazo do próprio WhatsApp: passando dele, a edição é recusada lá e a
	// tela mostraria "editada" sem que o cliente visse mudança nenhuma.
	const MINUTOS_PARA_EDITAR = 15;

	// O prazo é conferido quando o menu ABRE (efeito), não a cada render: ler o
	// relógio no corpo do componente é leitura impura e o resultado mudaria
	// sozinho entre um desenho e outro.
	const [dentroDoPrazo, setDentroDoPrazo] = useState(false);

	useEffect(() => {
		if (!menuOpen) return undefined;

		// Em timeout de 0 para não mudar estado no corpo do efeito (render em
		// cascata); o menu já está na tela quando isto roda.
		const conferir = setTimeout(() => {
			const minutos =
				(Date.now() - new Date(message.createdAt).getTime()) / 1000 / 60;

			setDentroDoPrazo(minutos < MINUTOS_PARA_EDITAR);
		}, 0);

		return () => clearTimeout(conferir);
	}, [menuOpen, message.createdAt]);

	const podeEditar =
		message.fromMe && !message.isDeleted && !message.mediaUrl && dentroDoPrazo;

	const salvarEdicao = async () => {
		const texto = textoEditado.trim();

		if (!texto || texto === message.body) {
			setEditando(false);
			return;
		}

		setSalvandoEdicao(true);

		try {
			await api.put(`/messages/${message.id}`, { body: texto });
			toast.success("Mensagem editada.");
			setEditando(false);
		} catch (err) {
			toastError(err);
		}

		setSalvandoEdicao(false);
	};

	const apagarMensagem = async () => {
		try {
			await api.delete(`/messages/${message.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	const responder = () => {
		setReplyingMessage(message);
		handleClose();
	};

	const reagir = async (emoji) => {
		handleClose();

		try {
			await api.post(`/messages/${message.id}/reaction`, { emoji });
		} catch (err) {
			toastError(err);
		}
	};

	// Guardar na biblioteca a figurinha que o cliente mandou, para a equipe
	// responder com ela depois — é o "adicionar aos favoritos" do WhatsApp.
	const salvarFigurinha = async () => {
		handleClose();

		try {
			await api.post(`/figurinhas/da-mensagem/${message.id}`);
			toast.success("Figurinha salva. Ela já está no painel de figurinhas.");
		} catch (err) {
			toastError(err);
		}
	};

	const abrirConfirmacao = () => {
		setConfirmationOpen(true);
		handleClose();
	};

	const rect = anchorEl?.getBoundingClientRect();

	return (
		<>
			<ForwardMessageModal
				open={encaminhar}
				onClose={() => setEncaminhar(false)}
				message={message}
			/>
			{/* Editar a mensagem. Janela simples: o que se corrige aqui é um
			    erro de digitação, não se reescreve um texto longo. */}
			<Dialog open={editando} onOpenChange={(o) => !o && setEditando(false)}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Editar mensagem</DialogTitle>
					</DialogHeader>

					<textarea
						value={textoEditado}
						onChange={(e) => setTextoEditado(e.target.value)}
						rows={4}
						autoFocus
						disabled={salvandoEdicao}
						className="w-full resize-none rounded-lg border bg-background px-3 py-2 text-sm focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-60"
					/>

					<p className="text-xs text-muted-foreground">
						O cliente vê a mensagem corrigida com a marca de editada. O texto
						anterior fica guardado no histórico do atendimento.
					</p>

					<div className="flex justify-end gap-2">
						<Button
							variant="ghost"
							onClick={() => setEditando(false)}
							disabled={salvandoEdicao}
						>
							Cancelar
						</Button>
						<Button
							onClick={salvarEdicao}
							disabled={salvandoEdicao || !textoEditado.trim()}
							className="gap-2"
						>
							{salvandoEdicao && <Loader2 className="h-4 w-4 animate-spin" />}
							Salvar
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			<ConfirmationModal
				title={i18n.t("messageOptionsMenu.confirmationModal.title")}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={apagarMensagem}
			>
				{i18n.t("messageOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>

			{menuOpen &&
				rect &&
				createPortal(
					<div
						ref={menuRef}
						role="menu"
						style={{
							position: "fixed",
							top: rect.bottom + 4,
							// Alinha a borda direita do menu com a do botão.
							left: Math.max(8, rect.right - 176),
						}}
						className="z-50 w-44 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
					>
						{/* Emojis de uso frequente na primeira linha: reagir é o gesto
						    mais comum aqui e não deve custar dois cliques. O toque no
						    mesmo emoji desfaz — quem decide isso é o backend. */}
						{!message.isDeleted && (
							<div className="mb-1 flex items-center justify-between border-b px-1 pb-1.5">
								{RAPIDOS.map((emoji) => (
									<button
										key={emoji}
										type="button"
										title={`Reagir com ${emoji}`}
										onClick={() => reagir(emoji)}
										className="rounded-full px-1 text-lg leading-none transition-transform hover:scale-125"
									>
										{emoji}
									</button>
								))}
							</div>
						)}

						<button
							type="button"
							role="menuitem"
							onClick={responder}
							className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
						>
							<Reply className="h-4 w-4" />
							{i18n.t("messageOptionsMenu.reply")}
						</button>

						<button
							type="button"
							role="menuitem"
							onClick={() => {
								setEncaminhar(true);
								handleClose();
							}}
							className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
						>
							<Forward className="h-4 w-4" />
							Encaminhar
						</button>

						{message.mediaType === "sticker" && (
							<button
								type="button"
								role="menuitem"
								onClick={salvarFigurinha}
								className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
							>
								<Smile className="h-4 w-4" />
								Salvar figurinha
							</button>
						)}

						{/* Editar: só a nossa mensagem, e só enquanto o WhatsApp
						    aceita (15 min). Fora disso o item some, em vez de
						    oferecer algo que vai falhar. */}
						{podeEditar && (
							<button
								type="button"
								role="menuitem"
								onClick={() => {
									setEditando(true);
									setTextoEditado(message.body || "");
									handleClose();
								}}
								className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
							>
								<Pencil className="h-4 w-4" />
								Editar
							</button>
						)}

						{message.fromMe && (
							<button
								type="button"
								role="menuitem"
								onClick={abrirConfirmacao}
								className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive hover:bg-accent"
							>
								<Trash2 className="h-4 w-4" />
								Apagar para todos
							</button>
						)}
					</div>,
					document.body
				)}
		</>
	);
};

export default MessageOptionsMenu;
